import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { IImage } from 'tbk-components/src/components/Image';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Icon from 'tbk-components/src/components/Icon';
import cn from 'classnames';
import Button, { ILink } from 'tbk-components/src/components/Button';
import AdvancedMedia from '../AdvancedMedia';
import { useTranslation } from 'react-i18next';

export interface ISmallProductsI18n {
	youAreViewing?: string;
	resultsOf?: string;
}
export interface ISmallProductGridProps extends IBaseElementProps {
	title?: string;
	products?: ISmallProduct[];
	desktopStyle?: 'list' | 'grid';
	productSearchUrl?: ILink;
	total?: number;
	i18n?: ISmallProductsI18n;
}

export interface ISmallProduct extends IBaseElementProps {
	header?: string;
	image?: IImage;
	url?: ILink;
}

const SmallProductGrid: React.FC<ISmallProductGridProps> = ({
	className,
	classNames = [],
	products = [],
	title,
	desktopStyle = 'list',
	productSearchUrl,
	i18n,
	total,
}) => {
	const { t } = useTranslation();
	const ref = useRef<HTMLDivElement>(null);
	const [sideProductsCollapsed, setSideProductsCollapsed] =
		useState<boolean>(false);

	// This layoutEffect is required in order to set the sideProductsCollapsed state to true on initial render and properly set up the eventlistener.
	// Setting it to true by default will not render it properly in the lifecycle.

	useLayoutEffect(() => {
		if (desktopStyle !== 'grid') {
			setSideProductsCollapsed(true);
		}
	}, []);

	// This manipulates the dropdown state of the side bar products.
	// Because design shows at least 4 at all times, we must set the max-height of the dropdown to the height of the 4th product + the height of the 2nd product (which is 1 column) + 48px (padding)
	useEffect(() => {
		if (desktopStyle === 'grid' || !ref.current) {
			return;
		}

		const sideProducts = ref.current.querySelector(
			'.side-product-list-content',
		);
		const sideProductsList = sideProducts.querySelector('ul');
		const fourthProduct = sideProductsList.querySelector('li:nth-child(4)');
		const secondProduct = sideProductsList.querySelector('li:nth-child(2)');

		if (sideProductsList.querySelectorAll('li').length < 4) {
			return;
		}

		const setProductDropdownHeight = () => {
			const windowWidth = window.innerWidth;
			if (sideProductsCollapsed) {
				windowWidth > 991
					? (sideProductsList.style.maxHeight = '3000px')
					: (sideProductsList.style.maxHeight = `${
							fourthProduct.getBoundingClientRect().height +
							secondProduct.getBoundingClientRect().height +
							+48
					  }px`);
			} else {
				windowWidth < 991 && setSideProductsCollapsed(true);
			}
		};

		if (sideProductsCollapsed) {
			setProductDropdownHeight();
			window.addEventListener('resize', setProductDropdownHeight);
		} else {
			sideProductsList.style.maxHeight = '3000px';
		}
		return () => {
			window.removeEventListener('resize', setProductDropdownHeight);
		};
	}, [sideProductsCollapsed]);

	return (
		<div
			ref={ref}
			className={cn(
				className || 'text-center side-product-list',
				`side-product-list--${desktopStyle}`,
				...classNames,
			)}
		>
			{title && <h3 className={cn('h6')}>{title}</h3>}
			<div className="side-product-list-content">
				<ul
					className={cn(
						'p-0 list-unstyled',
						desktopStyle === 'list' && 'overflow-hidden',
					)}
				>
					{products.length > 0 &&
						products.map((product: ISmallProduct, index) => {
							const listContent = (
								<div
									className={cn(
										'd-flex ',
										desktopStyle === 'list' &&
											'gap-4 flex-column flex-lg-row',
										desktopStyle === 'grid' &&
											'gap-lg-4 gap-3 flex-row align-items-start align-items-lg-center flex-lg-column',
									)}
								>
									{product.image && (
										<AdvancedMedia
											image={product.image}
											mediaSize="cover"
											size="1:1"
											classNames={[
												desktopStyle === 'list' &&
													'mx-lg-0',
												'mw-85px mx-auto bg-white flex-shrink-0',
											]}
										/>
									)}
									{product.header && (
										<h6
											className={cn(
												'text-body-sm  mb-0 mt-1',
												desktopStyle === 'list' &&
													'text-md-start',
												desktopStyle === 'grid' &&
													'text-center',
											)}
										>
											{product.header}
										</h6>
									)}
								</div>
							);
							return (
								<li
									key={index}
									className={cn('p-2 small-product-item')}
								>
									{product.url?.href ? (
										<a
											href={product.url.href}
											className="text-decoration-none"
										>
											{listContent}
										</a>
									) : (
										listContent
									)}
								</li>
							);
						})}
				</ul>
				{desktopStyle !== 'grid' && products?.length > 4 && (
					<div className="d-block d-lg-none mobile-dropdown-list">
						<button
							className="btn d-flex gap-2 btn-sm mx-auto text-center btn-primary"
							onClick={() => setSideProductsCollapsed((s) => !s)}
						>
							{sideProductsCollapsed ? 'Show All' : 'Show Less'}
							<Icon
								name={
									!sideProductsCollapsed
										? 'chevron-up'
										: 'chevron-down'
								}
							/>
						</button>
					</div>
				)}
			</div>

			{(productSearchUrl || (total > 0 && products?.length > 0)) && (
				<div className="advanced-search-cta w-100 bg-light p-3">
					<div className="advanced-search-cta__container w-100 p-0 d-flex gap-4 flex-wrap justify-content-between">
						{total > 0 && products?.length > 0 && (
							<div className="total-result-counter me-0 ms-auto mb-3 d-flex flex-wrap gap-4">
								<p className="mb-0">
									{`${
										i18n?.youAreViewing || 'You are viewing'
									} ${products?.length}
											${i18n?.resultsOf || 'results of'} ${total}`}
								</p>

								{productSearchUrl && (
									<Button
										className="text-underline d-none d-md-flex mb-0"
										{...productSearchUrl}
										label="View All"
									/>
								)}
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default SmallProductGrid;
